import {useEffect, useState} from "react";
import {useNavigate} from "react-router-dom";
import {SessionAPI} from "../logic/api/session.api";
import {SESSION_STATUS} from "../logic/types/session.type";
import {useSessionStore} from "../store";
import {
  getArtistFromPath,
  getSessionPath,
  convertArtistToType,
} from "src/utils/artist.utils";
import {path} from "src/router/path";
import {isServiceUnavailableError} from "src/utils/error.utils";

type SessionStatus = keyof typeof SESSION_STATUS;

const ALLOWED_PATHS_BY_STATUS: Record<SessionStatus, readonly string[]> = {
  NONE: ["", "/waiting"],
  QUEUE: ["/queue"],
  CODE: ["/code"],
  PLAY: ["/character", "/play"],
};

export const useRouteGuard = (
  pathname: string,
): {isLoading: boolean; accessDenied: boolean} => {
  const [isLoading, setIsLoading] = useState(true);
  const [accessDenied, setAccessDenied] = useState(false);
  const navigate = useNavigate();

  const checkAccess = async () => {
    setIsLoading(true);
    const artist = getArtistFromPath(pathname);
    const type = convertArtistToType(artist);
    try {
      const response = await SessionAPI.fetchSessionStatus({query: {type}});

      const status = response.status;
      const sessionPath = getSessionPath(pathname);
      const allowed = ALLOWED_PATHS_BY_STATUS[status];
      if (!allowed.includes(sessionPath)) {
        setAccessDenied(true);
        return;
      }
      useSessionStore.getState().setSessionRemainingSeconds(response.ttl);
    } catch (error) {
      if (isServiceUnavailableError(error)) {
        navigate(path.error(artist), {replace: true});
        return;
      }

      const errorMessage = (error as Error).message;
      if (errorMessage === "tokenId must be a string") {
        setAccessDenied(true);
      }
      console.error(error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    checkAccess();
  }, []);

  return {isLoading, accessDenied};
};
